import { isBuiltInContextMenuVisible, isFallbackElement, isScriptAccessAllowed, isYoutubeFlashSource, RufflePlayer, workaroundYoutubeMixedContent, } from "./ruffle-player";
import { registerElement } from "./register-element";
import { isSwf } from "./swf-utils";
/**
 * A polyfill html element.
 *
 * This specific class tries to polyfill existing `<embed>` tags,
 * and should not be used. Prefer [[RufflePlayer]] instead.
 *
 * @internal
 */
export class RuffleEmbed extends RufflePlayer {
    /**
     * Constructs a new Ruffle flash player for insertion onto the page.
     *
     * This specific class tries to polyfill existing `<embed>` tags,
     * and should not be used. Prefer [[RufflePlayer]] instead.
     */
    constructor() {
        super();
    }
    /**
     * @ignore
     * @internal
     */
    connectedCallback() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v;
        super.connectedCallback();
        const src = this.attributes.getNamedItem("src");
        if (src) {
            const allowScriptAccess = (_b = (_a = this.attributes.getNamedItem("allowScriptAccess")) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
            const menu = (_d = (_c = this.attributes.getNamedItem("menu")) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : null;
            // Kick off the SWF download.
            this.load({
                url: src.value,
                allowScriptAccess: isScriptAccessAllowed(allowScriptAccess, src.value),
                parameters: (_f = (_e = this.attributes.getNamedItem("flashvars")) === null || _e === void 0 ? void 0 : _e.value) !== null && _f !== void 0 ? _f : null,
                backgroundColor: (_h = (_g = this.attributes.getNamedItem("bgcolor")) === null || _g === void 0 ? void 0 : _g.value) !== null && _h !== void 0 ? _h : null,
                base: (_k = (_j = this.attributes.getNamedItem("base")) === null || _j === void 0 ? void 0 : _j.value) !== null && _k !== void 0 ? _k : null,
                menu: isBuiltInContextMenuVisible(menu),
                salign: (_m = (_l = this.attributes.getNamedItem("salign")) === null || _l === void 0 ? void 0 : _l.value) !== null && _m !== void 0 ? _m : "",
                quality: (_p = (_o = this.attributes.getNamedItem("quality")) === null || _o === void 0 ? void 0 : _o.value) !== null && _p !== void 0 ? _p : "high",
                scale: (_r = (_q = this.attributes.getNamedItem("scale")) === null || _q === void 0 ? void 0 : _q.value) !== null && _r !== void 0 ? _r : "showAll",
                wmode: (_t = (_s = this.attributes.getNamedItem("wmode")) === null || _s === void 0 ? void 0 : _s.value) !== null && _t !== void 0 ? _t : "window" /* WindowMode.Window */,
                allowNetworking: (_v = (_u = this.attributes.getNamedItem("allowNetworking")) === null || _u === void 0 ? void 0 : _u.value) !== null && _v !== void 0 ? _v : "all" /* NetworkingAccessMode.All */,
            });
        }
    }
    /**
     * Polyfill of HTMLObjectElement.
     *
     * @ignore
     * @internal
     */
    get src() {
        var _a;
        return (_a = this.attributes.getNamedItem("src")) === null || _a === void 0 ? void 0 : _a.value;
    }
    /**
     * Polyfill of HTMLObjectElement.
     *
     * @ignore
     * @internal
     */
    set src(srcval) {
        if (srcval) {
            const attr = document.createAttribute("src");
            attr.value = srcval;
            this.attributes.setNamedItem(attr);
        }
        else {
            this.attributes.removeNamedItem("src");
        }
    }
    /**
     * @ignore
     * @internal
     */
    static get observedAttributes() {
        return ["src", "width", "height"];
    }
    /**
     * @ignore
     * @internal
     */
    attributeChangedCallback(name, oldValue, newValue) {
        var _a, _b, _c, _d;
        super.attributeChangedCallback(name, oldValue, newValue);
        if (this.isConnected && name === "src") {
            const src = this.attributes.getNamedItem("src");
            if (src) {
                this.load({
                    url: src.value,
                    parameters: (_b = (_a = this.attributes.getNamedItem("flashvars")) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null,
                    base: (_d = (_c = this.attributes.getNamedItem("base")) === null || _c === void 0 ? void 0 : _c.value) !== null && _d !== void 0 ? _d : null,
                });
            }
        }
    }
    /**
     * Checks if the given element may be polyfilled with this one.
     *
     * @param elem Element to check.
     * @returns True if the element looks like a Flash embed.
     */
    static isInterdictable(elem) {
        const src = elem.getAttribute("src");
        const type = elem.getAttribute("type");
        // Don't polyfill when no file is specified.
        if (!src) {
            return false;
        }
        // Don't polyfill if the element is inside a specific node.
        if (isFallbackElement(elem)) {
            return false;
        }
        // Don't polyfill when the file is a YouTube Flash source.
        if (isYoutubeFlashSource(src)) {
            // Workaround YouTube mixed content; this isn't what browsers do automatically, but while we're here, we may as well.
            workaroundYoutubeMixedContent(elem, "src");
            return false;
        }
        return isSwf(src, type);
    }
    /**
     * Creates a RuffleEmbed that will polyfill and replace the given element.
     *
     * @param elem Element to replace.
     * @returns Created RuffleEmbed.
     */
    static fromNativeEmbedElement(elem) {
        const externalName = registerElement("ruffle-embed", RuffleEmbed);
        const ruffleObj = document.createElement(externalName);
        ruffleObj.copyElement(elem);
        return ruffleObj;
    }
}
